/**
 * Toggles the collection facets sidebar for the "custom" filter direction (desktop).
 *
 * The Section Rendering API re-renders the whole `filters` block from scratch on every
 * filter change, and the server always renders the pane in its default (closed) state.
 * Without intervention, the morph that applies that response overwrites the open/closed
 * classes we set on click, making the panel appear to snap shut after every selection.
 *
 * We track the open/closed state ourselves (`#isOpen`) and watch the pane's `class`
 * attribute with a MutationObserver so we can immediately reapply that state whenever
 * the morph resets it — this doesn't depend on the section-render/morph lifecycle
 * calling back into this element, it just reacts to the DOM mutation directly.
 */
class FacetsCustomToggle extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {boolean} */
  #isOpen = false;

  /** @type {MutationObserver | null} */
  #paneObserver = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#button = this.querySelector('[data-facets-custom-toggle]');
    this.#pane = document.getElementById(this.dataset.paneId || '');
    this.#collectionWrapper = this.closest('.collection-wrapper');

    if (!(this.#button instanceof HTMLButtonElement) || !this.#pane) return;

    this.#collectionWrapper?.classList.add('collection-wrapper--has-facets-custom');
    this.#button.addEventListener('click', () => this.#toggle(), { signal });

    this.#paneObserver = new MutationObserver(() => this.#reconcilePaneState());
    this.#paneObserver.observe(this.#pane, { attributes: true, attributeFilter: ['class'] });

    this.#applyState();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#paneObserver?.disconnect();
  }

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #pane = null;

  /** @type {HTMLElement | null} */
  #collectionWrapper = null;

  /**
   * Reapplies our tracked open/closed state if a section re-render just reset the
   * pane's classes back to the server-rendered (closed) default.
   */
  #reconcilePaneState() {
    if (!this.#pane) return;

    const paneIsOpen = !this.#pane.classList.contains('hidden--desktop');
    if (paneIsOpen !== this.#isOpen) this.#applyState();
  }

  #toggle() {
    if (!this.#button || !this.#pane) return;

    this.#isOpen = !this.#isOpen;
    this.#applyState();
  }

  #applyState() {
    if (!this.#button || !this.#pane) return;

    this.#pane.classList.toggle('hidden--desktop', !this.#isOpen);
    this.#pane.classList.toggle('facets-block-wrapper--open', this.#isOpen);
    // `--has-facets-custom` is only ever added once, in connectedCallback, but a section
    // re-render's morph wipes it (along with `--facets-custom-open`) since the server-rendered
    // markup never includes it. The CSS rule that shifts the product grid beside the open pane
    // requires both classes together, so it must be re-asserted here every time we reconcile —
    // otherwise the grid falls back to its closed-state column and collides with the open pane.
    this.#collectionWrapper?.classList.add('collection-wrapper--has-facets-custom');
    this.#collectionWrapper?.classList.toggle('collection-wrapper--facets-custom-open', this.#isOpen);
    this.#button.setAttribute('aria-expanded', String(this.#isOpen));
    this.#sync();
  }

  #sync() {
    if (!this.#button) return;

    this.querySelectorAll('[data-facets-custom-toggle-label]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const showWhen = el.dataset.facetsCustomToggleLabel;
      const visible = (showWhen === 'open' && this.#isOpen) || (showWhen === 'closed' && !this.#isOpen);
      el.hidden = !visible;
    });

    this.querySelectorAll('[data-facets-custom-toggle-icon]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const showWhen = el.dataset.facetsCustomToggleIcon;
      const visible = (showWhen === 'open' && this.#isOpen) || (showWhen === 'closed' && !this.#isOpen);
      el.hidden = !visible;
    });
  }
}

if (!customElements.get('facets-custom-toggle')) {
  customElements.define('facets-custom-toggle', FacetsCustomToggle);
}
